import SessionStore from "../../../services/session/SessionStore";
import * as mobx from 'mobx'
import {debounce, difference, pickBy} from 'lodash'
import * as UrlFragmentSearch from "../../../util/UrlFragmentSearch";
import Amplitude from "./metrics/Amplitude";
import Heap from "./metrics/Heap";
import Kissmetrics from "./metrics/Kissmetrics";
import {mapUserInfo} from './metrics/MetricsProvider';
import Mixpanel from "./metrics/Mixpanel";
import Rudderstack from "./metrics/Rudderstack";

const QUEUE_PROCESSOR_TIMER = 3000;

let providers = [];

let lazyInstall = async () => {
  let triggerInstalled = () => {};
  let onceInstalled = new Promise(res => triggerInstalled = res);
  lazyInstall = () => onceInstalled;
  const { analytics } = window.APPLICATION_CONFIG || {};
  const { amplitude, heap, kissmetrics, mixpanel, rudderstack, rudderstackDataPlane } = analytics || {};
  const pendingProviders = []
    .concat(amplitude ? [new Amplitude(amplitude)] : [])
    .concat(heap ? new Heap(heap) : [])
    .concat(mixpanel ? new Mixpanel(mixpanel) : [])
    .concat(kissmetrics ? new Kissmetrics(kissmetrics) : [])
    .concat(rudderstack && rudderstackDataPlane ? new Rudderstack(rudderstack, rudderstackDataPlane) : []);
  const maybeInstalled = await Promise.all(
    pendingProviders
      .map(p => {
        return Promise.resolve(p.install())
          .then(() => p)
          .catch(x => {
            console.error("failed to install " + p.constructor.name, x);
            return undefined
          })
      }));
  providers = maybeInstalled.filter(x => x);
  triggerInstalled();
}


/** tracks navigation changes into url for primary navigation */
let pathQueue = [];
let previousPath;

const onHashChange = () => {
  pushPath();
  pushSearchParams();
}

const pushPath = () => {
  const pathAndQuery = UrlFragmentSearch.getPathAndQuery();
  const search = UrlFragmentSearch.getQueryObject();
  if(!previousPath || previousPath.pathname !== pathAndQuery.pathname) {
    pathQueue.push({
      pathname: pathAndQuery.pathname,
      ...search
    });
    pushWhenQuiescent();
    previousPath = pathAndQuery;
  }
}

const pushWhenQuiescent = debounce(() => {
  // only the last path is not skipped
  pathQueue.forEach((event, i) => {
    recordEvent('navigation', {
      skipped: i + 1 < pathQueue.length,
      path: event.pathname,
      search: event.search
    });
  });
  // reset
  pathQueue = [];
}, QUEUE_PROCESSOR_TIMER);

//tracks the parameter changes, debounces to three seconds
let previousPathname = "";
let previousSearch = {};

const pushSearchParams = debounce(() => {
  const pathname =  UrlFragmentSearch.getPathAndQuery().pathname;
  const search = UrlFragmentSearch.getQueryObject();
  if(pathname === previousPathname) {

    /**
     *
     * @returns {Object} object with the current value of all changed url parameters (including "undefined" for
     * parameters that have been nulled out
     */
    const getModifiedSearch = () => {
      const changedKeys = Object.keys({...search, ...previousSearch}).filter(k => {
        return previousSearch[k] !== search[k];
      })
      // return the current value of all changed keys
      return changedKeys
        .map(k => ({[k]: search[k] || "PARAMETER_REMOVED"}))
        .reduce((a, b) => ({...a, ...b}), {});
    }


    const modifiedSearch = getModifiedSearch();
    const anySearchChanged = Object.keys(modifiedSearch).length;

    if (anySearchChanged) {
      recordEvent('queryChanged', {
        path: pathname,
        search
      });
    }

  }

  previousPathname = pathname;
  previousSearch = search;
}, 3000);



let disposeMobxReaction
export function install() {
  uninstall()

  disposeMobxReaction = mobx.reaction(
    () => SessionStore.user,
    async user => {
      window.removeEventListener('hashchange', onHashChange);
      if (user) {
        await lazyInstall();
        window.addEventListener('hashchange', onHashChange);
        providers.forEach(p => {
          try {
            const userIdentity = user.impersonatedByUser
              ? `${user.impersonatedByUser.username}_impersonating_${user.username}`
              : user.username;
            p.identify(userIdentity, mapUserInfo(user))
          } catch (e) {
            console.error("failed to identify to " + p.constructor.name, e)
          }
        });
        onHashChange(); // push the initial page on login/page load
      }
    }, {
      fireImmediately: true
    });

}

export function uninstall() {
  window.removeEventListener('hashchange', onHashChange)
  disposeMobxReaction && disposeMobxReaction()
}

/** sends the event to all configured metrics sites */
export async function recordEvent(name, properties = {}) {
  await lazyInstall(); // make sure events can be recorded
  providers.forEach(p => {
    try {
      p.recordEvent(name, properties)
    } catch (e) {
      console.error("failed to record event to: " + p.constructor.name, e)
    }
  });
}




